import { ReactNode, useEffect } from 'react';
import { Modal, ModalProps } from '../../../../../../components/modal/modal';
import { Select } from '../../../../../../components/select/select';
import { useLang } from '../../../../../../hooks/use-lang';
import { IdValue } from '../../../../../../types/props';
import { FormCreateInput } from './form-create-input-types/form-create-input';
import { Button } from '../../../../../../components/button/button';
import s from './form-create-modal.module.css';
import { useFormStore } from '../../../../../../store/use-form-store';
import { FormElementType, FormElementTypeType } from '../../../../../../store/api/forms-api';
import { FormCreateSelect } from './form-create-input-types/form-create-select';
import { FormCreateModalHelper } from './form-create-modal-helper';
import { Container } from '../../../../../../components/container/container';
import { FormCreateDateTime } from './form-create-input-types/form-create-date-time';
import { FormCreateDateTimeRange } from './form-create-input-types/form-create-date-time-range';
import { FormCreateMultiSelect } from './form-create-input-types/form-create-multi-select';
import { FormCreateCheckbox } from './form-create-input-types/form-create-checkbox';
import { FormCreateSwitch } from './form-create-input-types/form-create-switch';
import { useInputLangStore } from '../../../../../../store/use-input-lang-store';
import { Label } from '../../../../../../components/label/label';
import { useQueryParams } from '../../../../../../hooks/use-query-params';
import { Divider } from '../../../../../../components/divider/divider';
import { elementsRecord } from '../../forms-helpers';

const elements: IdValue[] = Object.entries(elementsRecord).map(([key, value]) => ({
  id: key,
  value,
}));

const elementTypesRecord: Record<FormElementTypeType, ReactNode> = {
  input: <FormCreateInput />,
  select: <FormCreateSelect />,
  checkbox: <FormCreateCheckbox />,
  datetime: <FormCreateDateTime />,
  datetimeRange: <FormCreateDateTimeRange />,
  'multi-select': <FormCreateMultiSelect />,
  switch: <FormCreateSwitch />,
};

const initElement = (): FormElementType => {
  return {
    uuid: `temp-${Date.now().toString()}`,
    info: {
      helper: 'none',
    },
    type: 'input',
    order: 0,
  };
};

export const FormCreateModal = (props: ModalProps) => {
  const { t, getLocale } = useLang();
  const { handleUpdateNewElement, handleResetNewElement, newElement, handleUpdateForm, form } =
    useFormStore();
  const { language } = useInputLangStore();

  const elementId = useQueryParams().getQueryParams().elementId;

  useEffect(() => {
    if (elementId) {
      const element = form?.elements?.find((item) => item.uuid === elementId);
      handleUpdateNewElement(element || initElement());
    } else {
      handleUpdateNewElement({ ...initElement(), order: form?.elements?.length || 0 });
    }

    return () => {
      handleResetNewElement();
    };
  }, [elementId]);

  const handleCreateElement = () => {
    if (!newElement) return;
    const elements = form?.elements || [];
    if (elementId) {
      handleUpdateForm({
        elements: elements.map((item) => (item.uuid === elementId ? newElement : item)),
      });
    } else {
      handleUpdateForm({
        elements: [...elements, newElement],
      });
    }

    props.onClose();
  };

  const localisedElements = elements.map((item) => ({
    ...item,
    value: t(item.value),
  }));

  const selectedElement = localisedElements.find((item) => item.id === newElement?.type);

  return (
    <Modal {...props} title={t('Создание элемента')} withLang className={s.feature}>
      <Container label={t('Тип элемента')}>
        <Select
          options={localisedElements}
          onSelect={(e) => handleUpdateNewElement({ type: e.id as FormElementTypeType })}
          value={selectedElement || elements[0]}
        />
      </Container>
      <Divider />
      <FormCreateModalHelper />
      <Divider />

      <Container label={t('Элемент')}>
        {newElement?.info?.helper === 'label' && (
          <Label>{getLocale(newElement?.info?.helper_text, language)}</Label>
        )}
        {newElement ? elementTypesRecord[newElement.type!] : <span>что то не так</span>}
      </Container>
      <div className={s.controls}>
        <Button variant="secondary" onClick={props.onClose}>
          {t('Отмена')}
        </Button>
        <Button variant="primary" onClick={handleCreateElement}>
          {t(elementId ? 'Изменить' : 'Создать')}
        </Button>
      </div>
    </Modal>
  );
};
